import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useBookings, useRefreshAll } from "../../hooks/queries";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { formatTime, localName, money } from "../../lib/format";
import type { Booking } from "../../lib/types";
import { Empty, Pill, SectionHeader } from "../ui/Badges";
import { DownloadButton } from "../ui/DownloadButton";
import { PrintButton } from "../ui/PrintButton";
import { Modal } from "../ui/Modal";
import { PaymentSection, usePaymentForm } from "../ui/PaymentSection";

function PayBalanceDialog({ booking, onClose }: { booking: Booking; onClose: () => void }) {
  const { t } = useTranslation();
  const { toast, toastError } = useToast();
  const refresh = useRefreshAll();
  const form = usePaymentForm("cash");
  const [busy, setBusy] = useState(false);

  async function pay() {
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/payments`, { payments: form.payments(booking.balance) });
      toast(t("booking.paid", { ref: booking.refNo }), "success");
      refresh();
      onClose();
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal open onClose={onClose} title={t("booking.payBalance", { ref: booking.refNo })} size="sm"
      footer={<><button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button><button type="button" className="btn-primary" disabled={busy || !form.isValid(booking.balance)} onClick={pay}>{t("booking.collect", { amount: money(booking.balance) })}</button></>}>
      <div className="flex justify-between text-sm font-black"><span>{t("booking.remaining")}</span><span className="num font-mono text-cosmic-purple">{money(booking.balance)} {t("currency")}</span></div>
      <PaymentSection form={form} total={booking.balance} />
    </Modal>
  );
}

export function BookingsToday() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { isManager } = useAuth();
  const { toast, toastError } = useToast();
  const refresh = useRefreshAll();
  const { data, isLoading } = useBookings();
  const [paying, setPaying] = useState<Booking | null>(null);

  async function setStatus(b: Booking, status: "completed" | "cancelled") {
    try {
      await api.patch(`/bookings/${b.id}/status`, { status });
      toast(t(`booking.status.${status}`), "info");
      refresh();
    } catch (e) {
      toastError(e);
    }
  }

  return (
    <section className="card space-y-3 p-4 sm:p-5" aria-label={t("booking.today")}>
      <SectionHeader title={`📅 ${t("booking.today")}`} right={<Pill tone="amber">{t("booking.scheduled", { count: data?.length ?? 0 })}</Pill>} />
      {isLoading ? <Empty>{t("common.loading")}</Empty> : !data?.length ? <Empty>{t("booking.noneToday")}</Empty> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[10px] font-black uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-3 py-2 text-start">{t("booking.ref")}</th><th className="px-2 py-2 text-start">{t("booking.customer")}</th>
                <th className="px-2 py-2 text-start">{t("booking.when")}</th><th className="px-2 py-2 text-end">{t("booking.grandTotal")}</th>
                <th className="px-2 py-2 text-end">{t("booking.remaining")}</th><th className="px-2 py-2 text-center">{t("table.status")}</th><th className="px-3 py-2 text-end">{t("table.actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-bold text-gray-700">
              {data.map((b) => (
                <tr key={b.id} className={b.status === "cancelled" ? "opacity-50" : ""}>
                  <td className="px-3 py-3"><span className="num font-mono font-black">{b.refNo}</span></td>
                  <td className="px-2 py-3">
                    <div className="text-cosmic-navy">{b.organizationName ? localName({ name: b.organizationName, nameAr: b.organizationNameAr }, locale) : b.guardianName}</div>
                    <div className="text-[10px] font-normal text-gray-400">{b.honoreeName ? `🎂 ${b.honoreeName} • ` : ""}{b.roomName ? localName({ name: b.roomName, nameAr: b.roomNameAr }, locale) : ""} • <span className="num">{b.kidsCount}</span> {t("booking.kids")}</div>
                  </td>
                  <td className="num px-2 py-3 font-mono text-[11px]">{formatTime(b.slotStart, locale)} – {formatTime(b.slotEnd, locale)}</td>
                  <td className="num px-2 py-3 text-end font-mono">{money(b.total)}</td>
                  <td className={`num px-2 py-3 text-end font-mono ${b.balance > 0 ? "text-solar-orange" : "text-emerald-600"}`}>{money(b.balance)}</td>
                  <td className="px-2 py-3 text-center"><Pill tone={b.status === "confirmed" ? "purple" : b.status === "completed" ? "green" : "gray"}>{t(`booking.status.${b.status}`)}</Pill></td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {b.balance > 0 && b.status !== "cancelled" && <button type="button" className="btn-primary !px-2 !py-1 !text-[10px]" onClick={() => setPaying(b)}>{t("booking.pay")}</button>}
                      <DownloadButton path={`/documents/invoice/booking/${b.id}?locale=${locale}`} fallbackName={`Invoice-${b.refNo}.pdf`} className="btn-ghost !px-2 !py-1 !text-[10px]">📄</DownloadButton>
                      <PrintButton path={`/documents/invoice/booking/${b.id}?locale=${locale}`} className="btn-ghost !px-2 !py-1 !text-[10px]"><span className="sr-only">print</span></PrintButton>
                      {isManager && b.status === "confirmed" && <button type="button" className="btn-success !px-2 !py-1 !text-[10px]" onClick={() => setStatus(b, "completed")}>{t("booking.complete")}</button>}
                      {isManager && b.status === "confirmed" && <button type="button" className="btn-ghost !px-2 !py-1 !text-[10px] !text-red-600" onClick={() => setStatus(b, "cancelled")}>{t("booking.cancel")}</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {paying && <PayBalanceDialog booking={paying} onClose={() => setPaying(null)} />}
    </section>
  );
}
